import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Person } from '../../types';

const PEOPLE_COLLECTION = 'people';

export const peopleService = {
  // Get all people
  async getPeople(): Promise<Person[]> {
    try {
      const q = query(collection(db, PEOPLE_COLLECTION), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as Person[];
    } catch (error) {
      console.error('Error fetching people:', error);
      throw error;
    }
  },

  // Get person by email (for linking to user accounts later)
  async getPersonByEmail(email: string): Promise<Person | null> {
    try {
      const q = query(collection(db, PEOPLE_COLLECTION), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      } as Person;
    } catch (error) {
      console.error('Error fetching person by email:', error);
      throw error;
    }
  },

  // Add a new person
  async addPerson(personData: Omit<Person, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, PEOPLE_COLLECTION), {
        ...personData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding person:', error);
      throw error;
    }
  },

  // Update person profile
  async updatePerson(personId: string, updates: Partial<Person>): Promise<void> {
    try {
      const personRef = doc(db, PEOPLE_COLLECTION, personId);
      await updateDoc(personRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating person:', error);
      throw error;
    }
  },

  // Delete person
  async deletePerson(personId: string): Promise<void> {
    try {
      const personRef = doc(db, PEOPLE_COLLECTION, personId);
      await deleteDoc(personRef);
    } catch (error) {
      console.error('Error deleting person:', error);
      throw error;
    }
  },

  // Update person profile by email (useful for user account linking)
  async updatePersonByEmail(email: string, updates: Partial<Person>): Promise<void> {
    try {
      const person = await this.getPersonByEmail(email);
      if (person) {
        await this.updatePerson(person.id, updates);
      } else {
        throw new Error('Person not found with email: ' + email);
      }
    } catch (error) {
      console.error('Error updating person by email:', error);
      throw error;
    }
  },

  // Search people by skills (for complex filtering)
  async searchPeopleBySkills(skillCategories: { [category: string]: string[] }): Promise<Person[]> {
    try {
      // For now, we'll get all people and filter client-side
      // In production, you might want to use Firestore's array-contains-any for better performance
      const allPeople = await this.getPeople();
      
      return allPeople.filter(person => {
        // Check if person matches any of the selected skills in any category
        for (const [category, skills] of Object.entries(skillCategories)) {
          if (skills.length === 0) continue;
          
          let categoryMatches = false;
          const personSkills = person[category as keyof Person] as string[] || [];
          
          // Check if person has any of the selected skills in this category
          if (skills.some(skill => personSkills.includes(skill))) {
            categoryMatches = true;
          }
          
          // If this category has filters but person doesn't match, exclude them
          if (!categoryMatches) {
            return false;
          }
        }
        return true;
      });
    } catch (error) {
      console.error('Error searching people by skills:', error);
      throw error;
    }
  },
};